/**
 * Phase 7-F — Adjudicator membership detection.
 *
 * Calls the API at `/adjudicators/me/membership` (architect §2.1) and caches
 * the answer for 5 minutes in module-level memory + localStorage so the
 * sidebar can render synchronously on subsequent route changes without
 * flashing in/out.
 *
 * Returns `{ isAdjudicator, contracts, loading, error, refetch }`.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api/client';

const CACHE_TTL_MS = 5 * 60 * 1000;
const STORAGE_KEY = 'ida.adjudicator.membership.v1';

export interface AdjudicatorMembership {
  isAdjudicator: boolean;
  contracts: string[];
}

interface CacheEntry {
  fetchedAt: number;
  value: AdjudicatorMembership;
}

let memoryCache: CacheEntry | null = null;
let inFlight: Promise<AdjudicatorMembership> | null = null;

function readCache(): CacheEntry | null {
  if (memoryCache && Date.now() - memoryCache.fetchedAt < CACHE_TTL_MS) {
    return memoryCache;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (Date.now() - parsed.fetchedAt < CACHE_TTL_MS) {
      memoryCache = parsed;
      return parsed;
    }
  } catch {
    /* corrupt — ignore */
  }
  return null;
}

function writeCache(value: AdjudicatorMembership) {
  const entry: CacheEntry = { fetchedAt: Date.now(), value };
  memoryCache = entry;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    /* quota / private mode — best effort */
  }
}

async function fetchMembership(): Promise<AdjudicatorMembership> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const data = await apiFetch<Partial<AdjudicatorMembership>>(
        '/adjudicators/me/membership',
      );
      const value: AdjudicatorMembership = {
        isAdjudicator: Boolean(data?.isAdjudicator),
        contracts: Array.isArray(data?.contracts) ? data.contracts : [],
      };
      writeCache(value);
      return value;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

export interface UseAdjudicatorMembership {
  data: AdjudicatorMembership | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useAdjudicatorMembership(): UseAdjudicatorMembership {
  const cached = readCache();
  const [data, setData] = useState<AdjudicatorMembership | null>(cached?.value ?? null);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const value = await fetchMembership();
      setData(value);
    } catch (e) {
      // 401 swallowed by client; treat any other error as "not an adjudicator".
      setError(e instanceof Error ? e.message : 'membership check failed');
      setData({ isAdjudicator: false, contracts: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (cached) return; // synchronously hydrated
    void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading, error, refetch };
}

/** Force-clear the cache — useful after an admin grants/revokes the role. */
export function clearAdjudicatorMembershipCache(): void {
  memoryCache = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
